import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { contextCompanyIdSelector } from '../../../../../redux/context/company/selectors'
import dimensionQueryKeys from '../../../../../features/dimension/queries/dimensionQueryKeys'
import { useCreateDimension } from '../../../../../features/dimension/queries/useCreateDimension'
import { useUpdateDimension } from '../../../../../features/dimension/queries/useUpdateDimension'
import { useDeleteDimension } from '../../../../../features/dimension/queries/useDeleteDimension'
import { useDeleteDimensions } from '../../../../../features/dimension/queries/useDeleteDimensions'
import { useUpdateDimensions } from '../../../../../features/dimension/queries/useUpdateDimensions'
import { useUpdateDefaultDimension } from '../../../../../features/dimension/queries/useUpdateDefaultDimension'
import { useDeleteDefaultDimension } from '../../../../../features/dimension/queries/useDeleteDefaultDimension'
import {
  buildBlockReorderBatch,
  buildMoveToEdgeBatch,
  buildReorderBatch,
  buildReparentBatch,
  DimensionOrderPatch
} from '../utils/dimensionMutationBatch'
import { sortDeepestFirst } from '../utils/dimensionDeletePlan'

/**
 * Wraps every dimension mutation hook used by this page. The regular mutation hooks
 * (create/update/delete/batch-update/default) only invalidate the dimension list on success —
 * they don't know about the undo-list query, so without this wrapper the Undo button would stay
 * stale after an edit made here. Every action below additionally invalidates the undo-list query.
 */
export const useDimensionGridActions = () => {
  const companyId = useSelector(contextCompanyIdSelector)
  const queryClient = useQueryClient()

  const createDimension = useCreateDimension()
  const updateDimension = useUpdateDimension()
  const deleteDimension = useDeleteDimension()
  const deleteDimensions = useDeleteDimensions()
  const updateDimensions = useUpdateDimensions()
  const updateDefaultDimension = useUpdateDefaultDimension()
  const deleteDefaultDimension = useDeleteDefaultDimension()

  const invalidateUndoList = useCallback(() => {
    if (companyId) queryClient.invalidateQueries({ queryKey: dimensionQueryKeys.undo({ companyId }) })
  }, [companyId, queryClient])

  const create = useCallback(
    async (data: Partial<Dimension>) => {
      const result = await createDimension.mutateAsync(data as Dimension)
      invalidateUndoList()
      return result
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [createDimension, invalidateUndoList]
  )

  const rename = useCallback(
    async (dimension: Dimension, name: string) => {
      const result = await updateDimension.mutateAsync({ ...dimension, name })
      invalidateUndoList()
      return result
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateDimension, invalidateUndoList]
  )

  const duplicate = useCallback(
    async (dimension: Dimension, copySuffix: string) => {
      const result = await createDimension.mutateAsync({
        ...dimension,
        id: undefined as unknown as number,
        dimensionId: undefined as unknown as string,
        name: `${dimension.name} ${copySuffix}`,
        default: false
      })
      invalidateUndoList()
      return result
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [createDimension, invalidateUndoList]
  )

  const deleteOne = useCallback(
    async (dimensionId: string) => {
      await deleteDimension.mutateAsync(dimensionId)
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [deleteDimension, invalidateUndoList]
  )

  // A single batch DELETE (mirrors the batch PUT used by reorder/reparent below) - the backend
  // has no FK constraint tying parentDimensionId to a real relation, so send order doesn't matter
  // for the database, but deepest-first is kept anyway since it makes request payloads/logs read
  // the same way computeDeletePlan reasons about the selection.
  const massDelete = useCallback(
    async (flatList: Dimension[], dimensionIds: string[]) => {
      const ordered = sortDeepestFirst(flatList, dimensionIds)
      await deleteDimensions.mutateAsync(ordered)
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [deleteDimensions, invalidateUndoList]
  )

  const reorder = useCallback(
    async (flatList: Dimension[], dimensionId: string, direction: 'up' | 'down') => {
      const patch = buildReorderBatch(flatList, dimensionId, direction)
      if (patch.length === 0) return
      await updateDimensions.mutateAsync(patch as Dimension[])
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateDimensions, invalidateUndoList]
  )

  const reorderBlock = useCallback(
    async (flatList: Dimension[], dimensionIds: string[], direction: 'up' | 'down') => {
      const patch = buildBlockReorderBatch(flatList, dimensionIds, direction)
      if (patch.length === 0) return
      await updateDimensions.mutateAsync(patch as Dimension[])
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateDimensions, invalidateUndoList]
  )

  const moveToEdge = useCallback(
    async (flatList: Dimension[], dimensionIds: string[], edge: 'start' | 'end') => {
      const patch = buildMoveToEdgeBatch(flatList, dimensionIds, edge)
      if (patch.length === 0) return
      await updateDimensions.mutateAsync(patch as Dimension[])
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateDimensions, invalidateUndoList]
  )

  // Single-item reparent (row menu "Siirrä toisen alle…", inspector parent field) — real, unaffected.
  const moveUnder = useCallback(
    async (flatList: Dimension[], dimensionIds: string[], newParentDimensionId: string | null) => {
      const patch = buildReparentBatch(flatList, dimensionIds, newParentDimensionId)
      if (patch.length === 0) return
      await updateDimensions.mutateAsync(patch as Dimension[])
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateDimensions, invalidateUndoList]
  )

  const applyOrderPatch = useCallback(
    async (patch: DimensionOrderPatch[]) => {
      if (patch.length === 0) return
      await updateDimensions.mutateAsync(patch as unknown as Dimension[])
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateDimensions, invalidateUndoList]
  )

  const setDefault = useCallback(
    async (dimensionId: string) => {
      await updateDefaultDimension.mutateAsync(dimensionId)
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [updateDefaultDimension, invalidateUndoList]
  )

  const clearDefault = useCallback(
    async (dimensionId: string) => {
      await deleteDefaultDimension.mutateAsync(dimensionId)
      invalidateUndoList()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [deleteDefaultDimension, invalidateUndoList]
  )

  // Shared by the create/rename modal (submit-on-save) and the inspector panel (live autosave) so
  // the "did this actually change" check lives in exactly one place.
  const applyParentDimension = useCallback(
    async (flatList: Dimension[], dimension: Dimension, newParentDimensionId: string | null) => {
      if (newParentDimensionId === (dimension.parentDimensionId ?? null)) return
      await moveUnder(flatList, [dimension.dimensionId], newParentDimensionId)
    },
    [moveUnder]
  )

  const applyDefault = useCallback(
    async (dimension: Dimension, wantDefault: boolean) => {
      if (wantDefault === !!dimension.default) return
      if (wantDefault) await setDefault(dimension.dimensionId)
      else await clearDefault(dimension.dimensionId)
    },
    [setDefault, clearDefault]
  )

  return {
    create,
    rename,
    duplicate,
    deleteOne,
    massDelete,
    reorder,
    reorderBlock,
    moveToEdge,
    moveUnder,
    applyOrderPatch,
    setDefault,
    clearDefault,
    applyParentDimension,
    applyDefault,
    isCreating: createDimension.isPending,
    isRenaming: updateDimension.isPending,
    isDeleting: deleteDimension.isPending || deleteDimensions.isPending,
    isReordering: updateDimensions.isPending,
    isSettingDefault: updateDefaultDimension.isPending || deleteDefaultDimension.isPending
  }
}
